import React, { useState, useContext, useRef, useEffect } from 'react';

import './UserSettingsModal.scss';

import { Container, Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import defaultValues from '../config/defaultValues.json';
import api, { makeApiRequest } from '../utils/Api';
import { trackEvent } from '../utils/Analytics';
import { authContext } from '../contexts/AuthContext';
import {
  ACCEPTED_PHOTO_TYPES,
  Area,
  LoadedPhoto,
  MIN_PHOTO_SIDE,
  initialFor,
  loadPhoto,
  renderCroppedPhoto,
} from '../utils/ProfilePhoto';
import PhotoCropper from './PhotoCropper';
import JoinPlus from './JoinPlus';
import PlusFlair from './PlusFlair';
import TwoStepSettings from './TwoStepSettings';

const UserSettingsModal = (props) => {
  const { currentUser, isOpen, onToggleModal } = props;

  const [errorText, setErrorText] = useState('');
  const [successText, setSuccessText] = useState('');
  const [userName, setUserName] = useState(currentUser ? currentUser.username : '');

  const [photoBusy, setPhotoBusy] = useState(false);
  // A photo that has been picked and is being positioned, before it is sent.
  const [cropPhoto, setCropPhoto] = useState<LoadedPhoto | null>(null);

  // Deleting the account is tucked behind a link and asks for the password again.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Accounts made with Google sign-in may have no password. They confirm deleting with
  // their username instead, and can add a password here to sign in with email too.
  const [deleteUsername, setDeleteUsername] = useState('');
  const [addPasswordOpen, setAddPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const context = useContext(authContext);
  // The context copy, because it is the one that changes when a photo is saved here.
  const user = context.currentUser || currentUser;
  const profilePicUrl = user && user.profilePicUrl;
  const passwordless = !!user && user.hasPassword === false;

  // Only the picture is taken from the reply, so the rest of the stored user (their
  // players, the connected player) stays as /user/me last gave it.
  const applyPicture = (response: any) => {
    context.updateCurrentUser({ ...user, profilePicUrl: response.profilePicUrl });
  };

  // Let go of the picked photo when it is replaced, cancelled or the modal goes away.
  useEffect(() => () => cropPhoto && cropPhoto.release(), [cropPhoto]);
  useEffect(() => {
    if (!isOpen) setCropPhoto(null);
  }, [isOpen]);

  const handlePhotoPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    // Cleared so picking the same file again still fires a change.
    e.target.value = '';
    if (!file) {
      return;
    }
    try {
      setCropPhoto(await loadPhoto(file));
    } catch (error) {
      showError(error.message);
    }
  };

  const handleCroppedPhotoSaved = async (area: Area) => {
    setPhotoBusy(true);
    try {
      const photo = renderCroppedPhoto(cropPhoto, area);
      const response = await api.users('uploadProfileImage', 'POST', context.auth.token, { photo });
      applyPicture(response);
      trackEvent('profile_photo_saved');
      setCropPhoto(null);
      showSuccess('Photo saved.');
    } catch (error) {
      showError(error.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleRemovePhotoClicked = async () => {
    setPhotoBusy(true);
    try {
      const response = await api.users('removeProfileImage', 'POST', context.auth.token, null);
      applyPicture(response);
      showSuccess('Photo removed.');
    } catch (error) {
      showError(error.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const closePasswordPanel = () => {
    setAddPasswordOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleAddPasswordClicked = async () => {
    if (newPassword.length < 8) {
      showError('Your password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      showError('Your passwords do not match.');
      return;
    }
    if (!passwordless && !currentPassword) {
      showError('Enter your current password.');
      return;
    }
    setPasswordBusy(true);
    try {
      await makeApiRequest(
        'auth',
        'change-password',
        'POST',
        passwordless ? { newPassword } : { oldPassword: currentPassword, newPassword },
        context.auth.token,
      );
      context.updateCurrentUser({ ...user, hasPassword: true });
      closePasswordPanel();
      showSuccess(
        passwordless ? 'Password added. You can now sign in with your email too.' : 'Password changed.',
      );
    } catch (error) {
      showError(error.message);
    } finally {
      setPasswordBusy(false);
    }
  };

  const handleDeleteAccountClicked = async () => {
    if (passwordless ? !deleteUsername.trim() : !deletePassword) {
      setDeleteError(
        passwordless ? 'Type your username to delete your account.' : 'Enter your password to delete your account.',
      );
      return;
    }
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await api.users(
        'deleteAccount',
        'POST',
        context.auth.token,
        passwordless ? { username: deleteUsername.trim() } : { password: deletePassword },
      );
      trackEvent('account_deleted');
      // The server has already ended every session, so just forget this one.
      context.setUnauthStatus();
      window.location.href = '/';
    } catch (error) {
      setDeleteError(error.message);
      setDeleteBusy(false);
    }
  };

  const handleUpdateUserButtonClicked = (e: any) => {
    console.log('handleUpdatePlayerButtonClicked');

    if (!userName) {
      showError('Please provide a username.');
      return;
    }

    let obj = {
      username: userName,
    };

    const token = context.auth.token;

    api
      .users('update', 'POST', token, obj)
      .then((response) => {
        onToggleModal();
      })
      .catch((error) => {
        showError(error.message);
        console.log('There has been a problem with your edit user operation: ' + error.message);
      });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
  };

  const handleUserNameChange = (e: any) => {
    setUserName(e.target.value);
  };

  const showError = (errorText: string) => {
    setErrorText(errorText);
    setTimeout(hideError, 3000);
  };

  const hideError = () => {
    setErrorText('');
  };

  const showSuccess = (successText: string) => {
    setSuccessText(successText);
    setTimeout(hideSuccess, 3000);
  };

  const hideSuccess = () => {
    setSuccessText('');
  };

  let key = currentUser.id;
  let buttonTitle = 'Save';
  let modalTitle = 'Edit Profile';

  return (
    <div key={key}>
      <Modal isOpen={isOpen} toggle={onToggleModal} className={props.className} backdrop={'static'} keyboard={false}>
        <ModalHeader toggle={onToggleModal}>{modalTitle}</ModalHeader>
        <ModalBody>
          <Container>
            <div>
              {errorText && <Alert color="danger">{errorText}</Alert>}
              {successText && <Alert color="success">{successText}</Alert>}
            </div>
            {cropPhoto ? (
              <PhotoCropper
                photo={cropPhoto}
                busy={photoBusy}
                onCancel={() => setCropPhoto(null)}
                onSave={handleCroppedPhotoSaved}
              />
            ) : (
              <div className="profile-photo">
                <PlusFlair isPlus={!!(user && user.isPlus)}>
                  {profilePicUrl ? (
                    <img className="profile-photo-circle" src={profilePicUrl} alt="Your profile photo" />
                  ) : (
                    <div className="profile-photo-circle profile-photo-initial" aria-hidden="true">
                      {initialFor(user && user.username)}
                    </div>
                  )}
                </PlusFlair>
                <div className="profile-photo-actions">
                  <input
                    ref={fileInput}
                    type="file"
                    accept={ACCEPTED_PHOTO_TYPES}
                    onChange={handlePhotoPicked}
                    hidden
                  />
                  <Button color="danger" outline disabled={photoBusy} onClick={() => fileInput.current.click()}>
                    {photoBusy ? 'Saving…' : profilePicUrl ? 'Change photo' : 'Add a photo'}
                  </Button>
                  {profilePicUrl && (
                    <Button color="secondary" disabled={photoBusy} onClick={handleRemovePhotoClicked}>
                      {'Remove'}
                    </Button>
                  )}
                  <p className="profile-photo-hint">
                    {`Shown beside the songs you add. A JPG or PNG at least ${MIN_PHOTO_SIDE} by ${MIN_PHOTO_SIDE} pixels; you choose which part goes in the circle.`}
                  </p>
                </div>
              </div>
            )}
            {!!(user && user.email) && (
              <p className="profile-email">
                Signed in as <strong>{user.email}</strong>
                {passwordless ? ' with Google' : ''}
              </p>
            )}
            <form className="playerSettings-form" onSubmit={handleSubmit}>
              <label>
                <span>User Name:</span>
                <input
                  type="text"
                  maxLength={defaultValues.user.nameCharLimit}
                  value={userName}
                  onChange={handleUserNameChange}
                />
              </label>
            </form>
            <div className="add-password">
              {!addPasswordOpen ? (
                <button type="button" className="delete-account-link" onClick={() => setAddPasswordOpen(true)}>
                  {passwordless ? 'Add a password' : 'Change password'}
                </button>
              ) : (
                <div className="delete-account-panel">
                  {passwordless ? (
                    <p>You sign in with Google. Add a password to sign in with your email too.</p>
                  ) : (
                    <input
                      type="password"
                      value={currentPassword}
                      placeholder="Current password"
                      aria-label="Current password"
                      autoComplete="current-password"
                      onChange={e => setCurrentPassword(e.target.value)}
                    />
                  )}
                  <input
                    type="password"
                    value={newPassword}
                    placeholder="New password"
                    aria-label="New password"
                    autoComplete="new-password"
                    onChange={e => setNewPassword(e.target.value)}
                  />
                  <input
                    type="password"
                    value={confirmNewPassword}
                    placeholder="Retype new password"
                    aria-label="Retype new password"
                    autoComplete="new-password"
                    onChange={e => setConfirmNewPassword(e.target.value)}
                  />
                  <div className="delete-account-actions">
                    <Button color="secondary" disabled={passwordBusy} onClick={closePasswordPanel}>
                      {'Cancel'}
                    </Button>
                    <Button color="primary" disabled={passwordBusy} onClick={handleAddPasswordClicked}>
                      {passwordBusy ? 'Saving…' : passwordless ? 'Add password' : 'Change password'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <TwoStepSettings
              // Remounted when a password is added, so turning it on is offered at once.
              key={passwordless ? 'no-password' : 'password'}
              token={context.auth.token}
              email={user ? user.email : ''}
              hasPassword={!passwordless}
              onNotice={(tone, text) => (tone === 'ok' ? showSuccess(text) : showError(text))}
            />
            <JoinPlus
              token={context.auth.token}
              returnPath="/"
              onNotice={(tone, text) => (tone === 'ok' ? showSuccess(text) : showError(text))}
            />
            <div className="delete-account">
              {!deleteOpen ? (
                <button type="button" className="delete-account-link" onClick={() => setDeleteOpen(true)}>
                  Delete my account
                </button>
              ) : (
                <div className="delete-account-panel">
                  <p>
                    This deletes your account, your players and your favorites for good. Songs you already added stay in
                    the queue without your name. Bumps you have left are lost.
                  </p>
                  {passwordless ? (
                    <input
                      type="text"
                      value={deleteUsername}
                      placeholder="Type your username to confirm"
                      aria-label="Type your username to confirm"
                      autoComplete="off"
                      onChange={(e) => setDeleteUsername(e.target.value)}
                    />
                  ) : (
                    <input
                      type="password"
                      value={deletePassword}
                      placeholder="Your password"
                      aria-label="Your password"
                      autoComplete="current-password"
                      onChange={(e) => setDeletePassword(e.target.value)}
                    />
                  )}
                  {deleteError && <Alert color="danger">{deleteError}</Alert>}
                  <div className="delete-account-actions">
                    <Button
                      color="secondary"
                      disabled={deleteBusy}
                      onClick={() => {
                        setDeleteOpen(false);
                        setDeletePassword('');
                        setDeleteUsername('');
                        setDeleteError('');
                      }}
                    >
                      {'Keep my account'}
                    </Button>
                    <Button color="danger" disabled={deleteBusy} onClick={handleDeleteAccountClicked}>
                      {deleteBusy ? 'Deleting…' : 'Delete my account'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Container>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onToggleModal}>
            {'Cancel'}
          </Button>
          <Button color="primary" onClick={handleUpdateUserButtonClicked}>
            {buttonTitle}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default UserSettingsModal;
